import React, { useEffect, useState } from "react";
import { Box, CircularProgress } from "@mui/material";
import AmCharts from "../../chart/amcharts";
import { GRAPH_TYPE } from "../../../constants/trendGraph/trendGraphType";
import { DEFAULT_LINE_COLORS } from "../../../constants/trendGraph/linesDefaultColors";
import { CHANNEL_TYPE, isAnalogChannel } from "../../../constants/channel/config";
import { useSelector } from "react-redux";
import { getDashboardCharts } from "../../../model/slice/customDashboardSlice";
import { channelNamesAndConfig } from "../../../model/slice/channelSlice";
import { formatCustomGraphDataForCSV } from "../../../util/trendPage/csvDownload";
import { createCSV, createCsvDownload } from "../../../helpers/csvDownload/createCSV";
import { selectedTimezone } from "../../../model/slice/timezoneSlice";
import { getCurrentFormattedDateTimeTextWithTimezoneForCSV } from "../../../util/timezone";
import * as PropTypes from "prop-types";
import { customTrendDataProcess } from "../../../util/trendDataProcess/custamTrendDataProcess";
import { fetchChannelDataByChartId } from "../../../api/channel";
import { calDurations } from "../../../constants/calDuration";

const onCSVButtonClickHandler = async ({
  graphData,
  chartLines,
  vesselName,
  chartName,
  timezone,
}) => {
  const processedData = formatCustomGraphDataForCSV(graphData, chartLines);
  const blob = await createCSV(processedData, ",");
  const fileName = `${vesselName}_custom_${chartName}_${getCurrentFormattedDateTimeTextWithTimezoneForCSV(timezone)}`;
  createCsvDownload({ blob, filename: fileName });
};

export const CustomTrendWidget = ({ vessel, chartId, index, startDate, endDate }) => {
  const timezone = useSelector(selectedTimezone);
  const channelNameAndConfig = useSelector(channelNamesAndConfig);
  const chartsSettings = useSelector(getDashboardCharts);
  const chartSettings = chartsSettings.charts.find((chart) => chart.id === chartId);
  const [chartData, setChartData] = useState([]);
  const [isFetching, setIsFetching] = useState(true);

  useEffect(() => {
    if (!vessel) return;

    const fetchData = async () => {
      setIsFetching(true);
      const controller = new AbortController();
      const signal = controller.signal;
      const interval = calDurations({ startDate, endDate });
      const data = await fetchChannelDataByChartId(
        vessel.id,
        chartId,
        startDate,
        endDate,
        interval,
        "",
        "",
        "",
        "default",
        signal
      );
      const { returnData, returnDataDigital } = customTrendDataProcess(data);
      setChartData({ chartId, graphData: returnData, digitalData: returnDataDigital });
      setIsFetching(false);
    };

    fetchData();
  }, [startDate, endDate]);

  const chartLines = chartSettings.lines?.map((line, idx) => {
    const lineConfig = channelNameAndConfig.find((config) => config?.chNo === line.chNo);
    return {
      key: `${line.chNo}`,
      keyName: `${line.chNo}`,
      name: `${lineConfig?.chName}`,
      color: DEFAULT_LINE_COLORS[idx],
      enableVisibilityIcon: "hidden",
      enable: true,
      channelType: isAnalogChannel(lineConfig?.chType) ? CHANNEL_TYPE.ANALOG : CHANNEL_TYPE.DIGITAL,
      digitalVar: line.digitalVar,
    };
  });

  return (
    <Box sx={{ height: "100%", padding: "8px 16px" }}>
      <h2 style={{ color: "white" }}>{chartSettings.name}</h2>
      <button
        name="csvDownloadButton"
        style={{ visibility: "hidden" }}
        onClick={() => {
          onCSVButtonClickHandler({
            graphData: chartData?.graphData,
            chartLines,
            vesselName: vessel.name,
            chartName: chartSettings.name,
            timezone,
          });
        }}
      />
      <Box sx={{ height: "calc(100% - 30px)", minHeight: "100px" }}>
        {isFetching ? (
          <CircularProgress />
        ) : (
          <AmCharts
            graphData={chartData?.graphData}
            graphDataDigital={chartData?.digitalData}
            vesselId={vessel.id}
            chartType={GRAPH_TYPE.CUSTOM}
            type={GRAPH_TYPE.CUSTOM}
            isComparison={false}
            vesselData={vessel}
            customChartLines={chartLines}
            isMockPreview={false}
            expand={true}
            multipleGraphIndex={index}
            isVesselOverview={false}
            isDashboard={true}
            overviewTrendIdx={0}
          />
        )}
      </Box>
    </Box>
  );
};

CustomTrendWidget.propTypes = {
  vessel: PropTypes.object.isRequired,
  chartId: PropTypes.string,
  index: PropTypes.number,
  startDate: PropTypes.object,
  endDate: PropTypes.object,
};
